import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  IconButton,
  Button,
  LinearProgress,
  Chip,
  Stack,
  Tooltip,
  Alert,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  Favorite as FavoriteIcon,
  FavoriteBorder as FavoriteBorderIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import Rating from '@mui/material/Rating';
import { contestAPI } from '../services/api';

/*
  VotingPage — UI for anonymous text voting ("Студент року")
  Requirements covered:
  - Show one text at a time with ability to switch between texts
  - Rating from 0 to 10 with 0.5 step (visualized as 5 hearts with halves)
  - User must click Save to lock the vote; afterwards cannot edit
  - Order of texts is random per user (handled by backend endpoint)
  - Progress shown; vote is counted only after all are rated (backend logic)
*/

const formatProgress = (voted, total) => `${voted}/${total}`;

const VotingPage = () => {
  const [submissions, setSubmissions] = useState([]); // [{id, contestId, text}]
  const [idx, setIdx] = useState(0);
  const [ratings, setRatings] = useState({}); // local tentative ratings (0..5 precision 0.5)
  const [locked, setLocked] = useState({}); // {submissionId: true}
  const [saving, setSaving] = useState(false);
  const [progress, setProgress] = useState({ totalSubmissions: 0, votedCount: 0, complete: false });
  const [error, setError] = useState(null);

  const current = submissions[idx] || null;

  const loadData = useCallback(async () => {
    try {
      setError(null);
      const [subsRes, progRes, votesRes] = await Promise.all([
        contestAPI.getActiveSubmissionsRandom(),
        contestAPI.getActiveProgress(),
        contestAPI.getActiveUserVotes(),
      ]);

      const subs = subsRes.data || [];
      setSubmissions(subs);


      // Hydrate ratings/locked from saved votes
      const votesArr = votesRes?.data || [];
      const lockedMap = {};
      const ratingsFromVotes = {};
      votesArr.forEach((v) => {
        if (!v) return;
        const sid = v.submissionId;
        if (sid == null) return;
        lockedMap[sid] = true;
        const score10 = Number(v.score);
        if (!Number.isNaN(score10)) {
          const val5 = Number((score10 / 2).toFixed(1));
          ratingsFromVotes[sid] = val5;
        }
      });

      // Restore local drafts for non-locked items
      let draft = {};
      if (typeof window !== 'undefined') {
        try {
          const raw = localStorage.getItem('votingDrafts_single');
          draft = raw ? JSON.parse(raw) : {};
        } catch (_) { /* ignore */ }
      }
      const mergedRatings = { ...ratingsFromVotes };
      subs.forEach((s) => {
        if (!lockedMap[s.id] && draft && draft[s.id] != null) {
          let v = Number(draft[s.id]);
          if (!Number.isNaN(v)) {
            // Normalize to 0..5 step 0.5
            v = Math.max(0, Math.min(5, Math.round(v * 2) / 2));
            mergedRatings[s.id] = v;
          }
        }
      });

      setRatings(mergedRatings);
      setLocked(lockedMap);

      // Set index to first unvoted submission (not locked), fallback to 0
      const firstIdx = subs.findIndex((s) => !lockedMap[s.id]);
      setIdx(firstIdx >= 0 ? firstIdx : 0);

      setProgress(progRes.data || { totalSubmissions: subs.length, votedCount: 0, complete: false });
    } catch (e) {
      console.error(e);
      setError('Не вдалося завантажити дані для оцінювання. Спробуйте пізніше.');
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const next = useCallback(() => {
    setIdx((p) => (p + 1 < submissions.length ? p + 1 : p));
  }, [submissions.length]);
  const prev = useCallback(() => {
    setIdx((p) => (p - 1 >= 0 ? p - 1 : p));
  }, []);

  // Keyboard navigation
  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'ArrowRight') next();
      if (e.key === 'ArrowLeft') prev();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [next, prev]);

  // Persist drafts for non-locked items into localStorage
  useEffect(() => {
    try {
      const key = 'votingDrafts_single';
      const toStore = {};
      Object.entries(ratings).forEach(([sid, val]) => {
        if (!locked[sid] && val !== undefined) {
          toStore[sid] = val;
        }
      });
      if (typeof window !== 'undefined') {
        localStorage.setItem(key, JSON.stringify(toStore));
      }
    } catch (_) { /* ignore */ }
  }, [ratings, locked]);

  const onChangeRating = (val5) => {
    if (!current) return;
    if (locked[current.id]) return; // do not allow editing after save
    // Prevent toggling the same heart to clear the rating back to 0 (MUI sends null)
    if (val5 == null) return;
    setRatings((r) => ({ ...r, [current.id]: val5 }));
  };

  const onSave = async () => {
    if (!current) return;
    const val5 = ratings[current.id];
    if (val5 === undefined) return; // don't save if user hasn't set a value explicitly
    const score10 = Number((val5 * 2).toFixed(1)); // convert to 0..10 in 0.5 step
    try {
      setSaving(true);
      setError(null);
      await contestAPI.voteActive(current.id, score10);
      setLocked((l) => ({ ...l, [current.id]: true }));
      // Refresh progress after successful save
      const prog = await contestAPI.getActiveProgress();
      setProgress(prog.data);
    } catch (e) {
      console.error(e);
      // If backend says already locked — reflect it in UI
      const message = e?.response?.data?.message || e?.message || 'Помилка збереження оцінки';
      setError(message);
      if (message.toLowerCase().includes('already locked')) {
        setLocked((l) => ({ ...l, [current.id]: true }));
      }
    } finally {
      setSaving(false);
    }
  };

  const ratingRef = useRef(null);

  const handleTouchStart = (e) => {
    // Mobile: compute half/whole heart by tap position
    if (!current) return;
    if (locked[current.id]) return;
    try {
      const touch = e.changedTouches && e.changedTouches[0];
      if (!touch) return;
      const el = ratingRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const x = Math.max(0, Math.min(rect.width, touch.clientX - rect.left));
      const max = 5; // hearts
      const precision = 0.5;
      const steps = max / precision; // 10 steps
      // Use ceil so tapping slightly to the right selects the next half
      const stepIndex = Math.min(steps, Math.max(0, Math.ceil((x / rect.width) * steps)));
      const val5 = Number((stepIndex * precision).toFixed(1));
      onChangeRating(val5);
      // Prevent ghost click
      e.preventDefault();
      e.stopPropagation();
    } catch (_) {
      // no-op
    }
  };

  const positionLabel = useMemo(() => {
    if (!submissions.length) return '';
    return `${idx + 1} / ${submissions.length}`;
  }, [idx, submissions.length]);

  return (
      <Box>
        <Typography variant="h4" sx={{ mb: 2, fontWeight: 700 }}>
          Оцінювання: «Студент року»
        </Typography>

        <Card sx={{ p: 1, mb: 2 }}>
          <CardContent>
            <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'stretch', sm: 'center' }} spacing={{ xs: 1.5, sm: 2 }} sx={{ mb: 1 }}>
              <Typography variant="body1" color="text.secondary" sx={{ textAlign: { xs: 'center', sm: 'inherit' } }}>
                Оцінено: {formatProgress(progress.votedCount || 0, progress.totalSubmissions || submissions.length || 0)}
              </Typography>
              <Box sx={{ flexGrow: 1, width: { xs: '100%', sm: 'auto' } }}>
                <LinearProgress
                    variant="determinate"
                    value={progress.totalSubmissions ? Math.min(100, ((progress.votedCount || 0) / progress.totalSubmissions) * 100) : 0}
                    sx={{ height: 10, borderRadius: 5 }}
                />
              </Box>
              {progress.complete && (
                  <Chip sx={{ alignSelf: { xs: 'center', sm: 'auto' } }} color="success" label="Ваші голоси зараховано" />
              )}
            </Stack>
            <Typography variant="caption" color="text.secondary">
              Після натискання «Зберегти» оцінку для конкретного тексту змінити буде неможливо.
            </Typography>
          </CardContent>
        </Card>

        {error && (
            <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        )}

        <Card>
          <CardContent>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
              <IconButton onClick={prev} disabled={idx === 0} aria-label="Попередній">
                <ArrowBackIcon />
              </IconButton>
              <Typography variant="subtitle2" color="text.secondary">{positionLabel}</Typography>
              <IconButton onClick={next} disabled={idx >= submissions.length - 1} aria-label="Наступний">
                <ArrowForwardIcon />
              </IconButton>
            </Stack>

            <Box sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', wordBreak: 'break-word', fontSize: '1.05rem', lineHeight: 1.6, mb: 3 }}>
              {current ? current.text : 'Наразі немає текстів для оцінювання.'}
            </Box>

            {current && (
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Tooltip title="Оцініть від 0 до 10 (0.5 крок)">
                      <Box ref={ratingRef} onTouchStart={handleTouchStart} sx={{ display: 'inline-flex' }}>
                        <Rating
                            name={`rating-${current.id}`}
                            icon={<FavoriteIcon fontSize="large" color="primary" />}
                            emptyIcon={<FavoriteBorderIcon fontSize="large" />}
                            precision={0.5}
                            max={5}
                            value={ratings[current.id] ?? 0}
                            onChange={(_, v) => onChangeRating(v)}
                            readOnly={!!locked[current.id]}
                            sx={{ fontSize: 40 }}
                        />
                      </Box>
                    </Tooltip>
                    <Typography variant="h6" sx={{ width: 72, textAlign: 'center' }}>
                      {((ratings[current.id] ?? 0) * 2).toFixed(1)} / 10
                    </Typography>
                  </Stack>

                  <Box sx={{ flexGrow: 1 }} />

                  {locked[current.id] ? (
                      <Chip label="Збережено" color="success" variant="outlined" />
                  ) : (
                      <Button
                          variant="contained"
                          startIcon={<SaveIcon />}
                          onClick={onSave}
                          disabled={saving || ratings[current.id] === undefined}
                      >
                        Зберегти
                      </Button>
                  )}
                </Stack>
            )}
          </CardContent>
        </Card>
      </Box>
  );
};

export default VotingPage;
